import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { SelfModel } from "@models/self.model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { AuthService } from "@services/auth.service";
import { SelfService } from "@services/self.service";
import {
  catchError,
  EMPTY,
  map,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs";

import * as AuthActions from "./auth.actions";
import { SELECT_MFA_TARGET, SELECT_MFA_TOKEN } from "./auth.selector";

@Injectable()
export class AuthEffects {
  initialLogin$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.DO_INITIAL_LOGIN),
      map((): Action => {
        return this.authService.isAuthenticated
          ? AuthActions.LOAD_USER()
          : AuthActions.SET_LOGIN_LOADING({ loading: false });
      })
    );
  });

  login$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.DO_LOGIN),

      switchMap(({ credentials, target }) => {
        return this.authService.login$(credentials).pipe(
          switchMap((response) => {
            if (this.authService.isMfaRequired(response)) {
              return of(
                AuthActions.LOGIN_MFA_REQUIRED({
                  mfaToken: response.mfaToken,
                  target,
                })
              );
            }

            return this.selfService.getSelf$().pipe(
              tap((): void => {
                void this.router.navigate([target]);
              }),

              map((user: SelfModel): Action => {
                return AuthActions.LOGIN_SUCCESS({ user });
              })
            );
          }),

          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.LOGIN_FAILED({ error }));
          })
        );
      })
    );
  });

  // Once the password step reports 2FA is required, look up whether the user already has an
  // authenticator: enrolled users are challenged for a code, others are sent through enrollment.
  setupMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.LOGIN_MFA_REQUIRED),

      switchMap(({ mfaToken }) => {
        return this.authService.setup2fa$(mfaToken).pipe(
          map((setup): Action => {
            return AuthActions.MFA_SETUP_LOADED({
              enrolled: setup.enrolled,
              sharedKey: setup.sharedKey,
              authenticatorUri: setup.authenticatorUri,
            });
          }),

          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.MFA_FAILED({ error }));
          })
        );
      })
    );
  });

  // Challenge: an enrolled user submits the code from their authenticator app.
  verifyMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.VERIFY_MFA),

      withLatestFrom(
        this.store.select(SELECT_MFA_TOKEN),
        this.store.select(SELECT_MFA_TARGET)
      ),

      switchMap(([{ code, rememberDevice }, mfaToken, target]) => {
        if (!mfaToken) {
          return of(AuthActions.CANCEL_MFA());
        }

        return this.authService.verify2fa$(mfaToken, code, rememberDevice).pipe(
          switchMap(() => this.completeLogin$(target)),
          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.MFA_FAILED({ error }));
          })
        );
      })
    );
  });

  // Enrollment: the user confirms setup with their first code; on success we get recovery codes.
  enableMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.ENABLE_MFA),

      withLatestFrom(this.store.select(SELECT_MFA_TOKEN)),

      switchMap(([{ code, rememberDevice }, mfaToken]) => {
        if (!mfaToken) {
          return of(AuthActions.CANCEL_MFA());
        }

        return this.authService.enable2fa$(mfaToken, code, rememberDevice).pipe(
          map((result): Action => {
            return AuthActions.MFA_ENABLED({
              recoveryCodes: result.recoveryCodes,
            });
          }),
          catchError((error: HttpErrorResponse): Observable<Action> => {
            return of(AuthActions.MFA_FAILED({ error }));
          })
        );
      })
    );
  });

  // After enrollment we hold the user on the recovery-codes screen; acknowledging finishes login.
  finalizeMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.MFA_RECOVERY_ACKNOWLEDGED),

      withLatestFrom(this.store.select(SELECT_MFA_TARGET)),

      switchMap(([, target]) => this.completeLogin$(target))
    );
  });

  // Recovery: complete login with a one-time recovery code instead of an app code.
  recoveryMfa$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.USE_RECOVERY_CODE),

      withLatestFrom(
        this.store.select(SELECT_MFA_TOKEN),
        this.store.select(SELECT_MFA_TARGET)
      ),

      switchMap(([{ recoveryCode, rememberDevice }, mfaToken, target]) => {
        if (!mfaToken) {
          return of(AuthActions.CANCEL_MFA());
        }

        return this.authService
          .recovery2fa$(mfaToken, recoveryCode, rememberDevice)
          .pipe(
            switchMap(() => this.completeLogin$(target)),
            catchError((error: HttpErrorResponse): Observable<Action> => {
              return of(AuthActions.MFA_FAILED({ error }));
            })
          );
      })
    );
  });

  loadUser$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.LOAD_USER),

      switchMap(() => {
        return this.selfService.getSelf$().pipe(
          map((user: SelfModel): Action => {
            return AuthActions.LOGIN_SUCCESS({ user });
          }),

          catchError((error: HttpErrorResponse): Observable<Action> => {
            this.authService.clearSession();

            return of(AuthActions.LOGIN_FAILED({ error }));
          })
        );
      })
    );
  });

  // The customer's own profile (GET /self) does not say who is behind the session; /auth/me does.
  // Asked after every user load, so a session that started as an impersonation hand-off is known
  // to be one.
  loadImpersonation$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.LOGIN_SUCCESS),

      switchMap(() => {
        return this.authService.currentUser$().pipe(
          map(
            (currentUser): Action =>
              AuthActions.IMPERSONATION_LOADED({
                isImpersonating: currentUser.isImpersonating,
              })
          ),

          catchError((): Observable<Action> => EMPTY)
        );
      })
    );
  });

  logout$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(AuthActions.DO_LOGOUT),

      switchMap(() => {
        return this.authService.logout$().pipe(
          tap((): void => {
            void this.router.navigate(["/login"]);
          }),

          map((): Action => {
            return AuthActions.LOGOUT_SUCCESS();
          }),

          catchError((): Observable<Action> => {
            this.authService.clearSession();
            void this.router.navigate(["/login"]);

            return of(AuthActions.LOGOUT_SUCCESS());
          })
        );
      })
    );
  });

  // Shared completion for the paths that hold real tokens (verify / recovery / finalize): load the
  // user and route to the originally-requested page.
  private completeLogin$(target: string | null): Observable<Action> {
    return this.selfService.getSelf$().pipe(
      tap((): void => {
        void this.router.navigate([target ?? "/"]);
      }),
      map((user: SelfModel): Action => AuthActions.LOGIN_SUCCESS({ user })),
      catchError((error: HttpErrorResponse): Observable<Action> => {
        this.authService.clearSession();
        return of(AuthActions.LOGIN_FAILED({ error }));
      })
    );
  }

  constructor(
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly selfService: SelfService,
    private readonly router: Router,
    private readonly store: Store
  ) {}
}
